var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { Schema, MapSchema, type } from "@colyseus/schema";
import { Appearance } from "./appearance.js";
/**
 * Authoritative Texas Hold'em table state, synced to every client as sub-state on
 * `GameState` (mirroring `GameState.blackjack`). The whole table lives in the ONE shared
 * world room — there is no separate Colyseus room.
 *
 * Design notes (privacy):
 * - Hole cards are PRIVATE. They are NOT carried in this synced schema while concealed —
 *   the server keeps the real two cards in a server-only map and pushes each owner their
 *   cards via a targeted `PokerHole` message. The seat only exposes `cardCount` (0/2) so
 *   everyone can render face-DOWN backs, and `revealed` which the server fills at showdown
 *   for the seats that go to the end (so the whole table sees the winning hands).
 * - Community cards are public; revealed progressively into `PokerTable.community`.
 * - Chips at the table are a per-seat stack (`chips`) bought in from the cash wallet; they
 *   are separate from the wallet and only convert back on cash-out / leave.
 *
 * NOTE: like `state.ts`, compiled with `tsc` (legacy decorators); must NOT be transformed
 * by esbuild/Vite — consumers import the built `dist/` output.
 */
/** One chair at the poker table. Pre-seeded for every index; `occupied` toggles use. */
export class PokerSeat extends Schema {
    constructor() {
        super(...arguments);
        this.occupied = false;
        /** Occupant display name + sprite (drives the seat portrait). */
        this.name = "";
        this.spriteId = 0;
        /** Occupant's layered appearance (copied from the Player on sit). */
        this.appearance = new Appearance();
        /** False while the occupant is disconnected mid-hand (their chips/cards still ride). */
        this.connected = true;
        /** Table chip stack (bought in from the wallet; cashes out on withdraw/leave). */
        this.chips = 0;
        /** Chips committed on the CURRENT betting street (resets each street). */
        this.bet = 0;
        /** Chips committed across the WHOLE hand (drives side-pot layering). */
        this.totalBet = 0;
        /** How many hole cards this seat holds (0 or 2) — clients render face-down backs. */
        this.cardCount = 0;
        /** Revealed hole cards at showdown (space-separated, e.g. "AS KD"); "" while concealed. */
        this.revealed = "";
        /** empty | out (seated, no chips in play) | active | folded | allin */
        this.status = "empty";
        /** Last action taken this hand (post | check | call | raise | fold | allin | ""), for table feedback. */
        this.lastAction = "";
        /** Whether this seat has acted in the current betting round (drives round-complete detection). */
        this.acted = false;
    }
}
__decorate([
    type("boolean"),
    __metadata("design:type", Object)
], PokerSeat.prototype, "occupied", void 0);
__decorate([
    type("string"),
    __metadata("design:type", Object)
], PokerSeat.prototype, "name", void 0);
__decorate([
    type("number"),
    __metadata("design:type", Object)
], PokerSeat.prototype, "spriteId", void 0);
__decorate([
    type(Appearance),
    __metadata("design:type", Object)
], PokerSeat.prototype, "appearance", void 0);
__decorate([
    type("boolean"),
    __metadata("design:type", Object)
], PokerSeat.prototype, "connected", void 0);
__decorate([
    type("number"),
    __metadata("design:type", Object)
], PokerSeat.prototype, "chips", void 0);
__decorate([
    type("number"),
    __metadata("design:type", Object)
], PokerSeat.prototype, "bet", void 0);
__decorate([
    type("number"),
    __metadata("design:type", Object)
], PokerSeat.prototype, "totalBet", void 0);
__decorate([
    type("number"),
    __metadata("design:type", Object)
], PokerSeat.prototype, "cardCount", void 0);
__decorate([
    type("string"),
    __metadata("design:type", Object)
], PokerSeat.prototype, "revealed", void 0);
__decorate([
    type("string"),
    __metadata("design:type", Object)
], PokerSeat.prototype, "status", void 0);
__decorate([
    type("string"),
    __metadata("design:type", Object)
], PokerSeat.prototype, "lastAction", void 0);
__decorate([
    type("boolean"),
    __metadata("design:type", Object)
], PokerSeat.prototype, "acted", void 0);
export class PokerTable extends Schema {
    constructor() {
        super(...arguments);
        /** idle | waiting (pre-hand join/buy-in window) | preflop | flop | turn | river | showdown | reset */
        this.phase = "idle";
        /** End-of-hand result shown to everyone, e.g. "Ghost wins $1,200 with Two Pair" ("" between results). */
        this.result = "";
        /** At a SHOWDOWN, the winner's two hole cards (space-joined, e.g. "AS KH") so the client can show what
         *  they were holding alongside the result. Empty for uncontested (no-showdown) wins and between hands. */
        this.resultCards = "";
        /** Revealed community cards, space-separated (0, 3, 4, then 5 cards). */
        this.community = "";
        /** Total chips in the pot (all committed bets this hand). */
        this.pot = 0;
        /** Amount a seat must match to stay in on the current street (the "to-call" level). */
        this.currentBet = 0;
        /** Minimum raise increment on the current street. */
        this.minRaise = 0;
        /** Dealer-button seat index (blinds + deal order rotate around it), or -1. */
        this.button = -1;
        /** Seat index whose turn it is to act, or -1 when no seat is acting. */
        this.activeSeat = -1;
        /** Server-clock ms at which the current phase/turn timer expires (client countdown). */
        this.timerEndsAt = 0;
        /** The chairs, keyed "0".."POKER_SEATS-1". */
        this.seats = new MapSchema();
    }
}
__decorate([
    type("string"),
    __metadata("design:type", Object)
], PokerTable.prototype, "phase", void 0);
__decorate([
    type("string"),
    __metadata("design:type", Object)
], PokerTable.prototype, "result", void 0);
__decorate([
    type("string"),
    __metadata("design:type", Object)
], PokerTable.prototype, "resultCards", void 0);
__decorate([
    type("string"),
    __metadata("design:type", Object)
], PokerTable.prototype, "community", void 0);
__decorate([
    type("number"),
    __metadata("design:type", Object)
], PokerTable.prototype, "pot", void 0);
__decorate([
    type("number"),
    __metadata("design:type", Object)
], PokerTable.prototype, "currentBet", void 0);
__decorate([
    type("number"),
    __metadata("design:type", Object)
], PokerTable.prototype, "minRaise", void 0);
__decorate([
    type("number"),
    __metadata("design:type", Object)
], PokerTable.prototype, "button", void 0);
__decorate([
    type("number"),
    __metadata("design:type", Object)
], PokerTable.prototype, "activeSeat", void 0);
__decorate([
    type("number"),
    __metadata("design:type", Object)
], PokerTable.prototype, "timerEndsAt", void 0);
__decorate([
    type({ map: PokerSeat }),
    __metadata("design:type", Object)
], PokerTable.prototype, "seats", void 0);
//# sourceMappingURL=poker.js.map